import React, { useEffect, useRef, useState } from "react";

import signup from "../../images/signup.jpeg";
import pay from "../../images/pay.jpeg";
import savebroker from "../../images/save-broker.jpeg";
import moniter from "../../images/moniter.jpeg";
import runstrategy from "../../images/runstrat.jpg";
import bg1 from "../../images/bg1.png";
import bg2 from "../../images/bg2.png";
import bg3 from "../../images/bg3.png";
import bg4 from "../../images/bg4.png";

// Reveal content logic
const revealContentUtils = {
  images: [
    {
      src: signup,
      title: "Create account",
      background: bg1,
      description: "After installing the app, create an account in algoTurtle.",
    },
    {
      src: pay,
      title: "Complete subscription",
      background: bg3,
      description: "Complete the subscription to access algoTurtle services.",
    },
    {
      src: savebroker,
      title: "Save your broker",
      background: bg2,
      description: "Save your broker info to activate your strategy access.",
    },
    {
      src: runstrategy,
      title: "Run your strategies",
      background: bg4,
      description: "Run your strategies seamlessly through our platform.",
    },
    {
      src: moniter,
      title: "Monitor your strategies",
      background: bg1,
      description: "Monitor your strategies and get real-time updates.",
    },
  ],
  calculateImagePosition: (index, containerRef, scrollPosition) => {
    if (!containerRef.current) return 0;

    const containerHeight = 300;
    const totalScroll =
      document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress =
      (scrollPosition / totalScroll) * revealContentUtils.images.length;

    return (
      ((index - scrollProgress) * containerHeight) %
      (revealContentUtils.images.length * containerHeight)
    );
  },
  calculateOpacity: (index, containerRef, scrollPosition) => {
    if (!containerRef.current) return 0;

    const position = revealContentUtils.calculateImagePosition(
      index,
      containerRef,
      scrollPosition
    );
    const containerHeight = 300;

    if (position < -containerHeight || position > containerHeight) return 0;
    if (position < 0) return 1 + position / containerHeight;
    if (position > 0) return 1 - position / containerHeight;
    return 1;
  },
  calculateBackgroundOpacity: (index, scrollPosition) => {
    const totalScroll =
      document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress =
      (scrollPosition / totalScroll) * (revealContentUtils.images.length - 1);
    const distance = Math.abs(index - scrollProgress);
    return Math.max(0, 1 - distance);
  },
};

const RevealContent = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrollPosition(y);

      const totalScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress =
        (y / totalScroll) * revealContentUtils.images.length;

      const currentIndex = Math.floor(scrollProgress);
      const sectionStart = currentIndex / revealContentUtils.images.length;
      const sectionEnd = (currentIndex + 1) / revealContentUtils.images.length;
      const sectionScrollProgress =
        (y - sectionStart * totalScroll) /
        ((sectionEnd - sectionStart) * totalScroll);

      let newIndex = currentIndex;
      if (sectionScrollProgress >= 0.6) {
        newIndex = currentIndex + 1;
      }

      setActiveIndex(
        Math.max(0, Math.min(newIndex, revealContentUtils.images.length - 1))
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-[300vh] bg-gray-100 relative">
      {/* Background Layers */}
      {revealContentUtils.images.map((image, index) => (
        <div
          key={`bg-${image.src}`}
          className="fixed inset-0 w-full h-screen bg-cover bg-center transition-opacity duration-1000 ease-in-out"
          style={{
            backgroundImage: `url(${image.background})`,
            opacity: revealContentUtils.calculateBackgroundOpacity(
              index,
              scrollPosition
            ),
          }}
        />
      ))}

      {/* Content Container */}
      <div className="fixed inset-0 z-10 flex flex-col-reverse sm:flex-row items-center justify-between px-6 sm:px-12 py-8 gap-4">
        {/* Left Side Content */}
        <div className="w-full sm:w-1/2 max-w-[600px]">
          <div
            key={`content-${revealContentUtils.images[activeIndex].src}`}
            className="transition-all duration-500 bg-white/80  sm:bg-transparent  rounded-xl p-4 sm:p-6 "
          >
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 text-center sm:text-left break-words">
              {revealContentUtils.images[activeIndex].title}
            </h2>
            <p className="text-sm sm:text-base text-gray-700 text-center sm:text-left leading-relaxed break-words">
              {revealContentUtils.images[activeIndex].description}
            </p>
          </div>
        </div>

        {/* Right Side Image Container */}
        <div
          ref={containerRef}
          className="relative mt-20 sm:mt-16 w-[260px] h-[460px] sm:w-[220px] sm:h-[380px] overflow-hidden bg-black rounded-2xl p-2 shadow-lg"
          style={{
            backgroundImage: `url(${revealContentUtils.images[activeIndex].background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {revealContentUtils.images.map((image, index) => (
            <div
              key={image.src}
              className="absolute w-[240px] h-[440px] sm:w-[200px] sm:h-[360px] transition-transform duration-400 ease-out rounded-xl overflow-hidden left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
              style={{
                transform: `translate(-50%, calc(${revealContentUtils.calculateImagePosition(
                  index,
                  containerRef,
                  scrollPosition
                )}px - 50%))`,
                opacity: revealContentUtils.calculateOpacity(
                  index,
                  containerRef,
                  scrollPosition
                ),
              }}
            >
              <img
                src={image.src}
                alt={image.title}
                className="w-full h-full fill rounded-2xl"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RevealContent;
